import { getSpeakerLayoutBorderKey } from '@goldcast/utils';
import { CSSProperties, useMemo } from 'react';
import {
  PREVIEW_FOCUS_POSTER,
  PREVIEW_VIDEO_POSTER
} from '@/Pages/VideoImportPage/MockTemplateBackgroundTransition/constants';
import { Clip } from '@/player-config/src/types/clip-types';
import { BaseConfig, layoutConfigMap } from '@/player-config/src/types/speaker-types';
import { getCropStyles } from '@/player-config/src/utils/cropUtils';

interface FocusLayoutPreviewProps {
  clip: Clip;
}

const sources = [PREVIEW_FOCUS_POSTER, PREVIEW_VIDEO_POSTER, PREVIEW_VIDEO_POSTER];

/**
 * Preview component for FOCUS layout in interim thumbnails.
 * Shows 1 large speaker on the left and 2 smaller speakers stacked vertically on the right.
 */
export default function FocusLayoutPreview({ clip }: FocusLayoutPreviewProps) {
  const backgroundStyle = useMemo(() => {
    if (!clip.asset_metadata.magicLayout?.backgroundImage) {
      return {
        backgroundColor: clip.asset_metadata.magicLayout?.backgroundColor || '#000000'
      };
    }
    return { backgroundImage: `url(${clip.asset_metadata.magicLayout?.backgroundImage})` };
  }, [clip.asset_metadata.magicLayout?.backgroundImage, clip.asset_metadata.magicLayout?.backgroundColor]);

  const finalConfig = useMemo(() => {
    const size = clip.asset_metadata.size;
    const layout = 'FOCUS';
    const fitMode = clip.asset_metadata.fit || 'FIT';
    const hideBorders = clip.asset_metadata.hide_borders ?? false;
    const numberOfSpeakers = 3;

    const config =
      layoutConfigMap[size]?.[layout]?.[fitMode]?.[getSpeakerLayoutBorderKey(!hideBorders)]?.[numberOfSpeakers];

    if (config) {
      config[0] =
        layoutConfigMap[size]?.[layout]?.['FIT']?.[getSpeakerLayoutBorderKey(!hideBorders)]?.[numberOfSpeakers]?.[0];
      const playerAspectRatio =
        clip.asset_metadata.size === 'SQUARE' ? 1 : clip.asset_metadata.size === 'PORTRAIT' ? 9 / 16 : 16 / 9;
      config[1].style = getCropStyles(
        {
          top_left: {
            x: 0.1073,
            y: 0.3074
          },
          bottom_right: {
            x: 0.4177,
            y: 0.737
          }
        },
        {
          width: (config[1].containerStyle as CSSProperties).width,
          height: (config[1].containerStyle as CSSProperties).height
        },
        playerAspectRatio
      ) as BaseConfig['style'];
      config[2].style = getCropStyles(
        {
          top_left: {
            x: 0.6052,
            y: 0.287
          },
          bottom_right: {
            x: 0.9406,
            y: 0.7444
          }
        },
        {
          width: (config[2].containerStyle as CSSProperties).width,
          height: (config[2].containerStyle as CSSProperties).height
        },
        playerAspectRatio
      ) as BaseConfig['style'];
      return config;
    }

    return [];
  }, [clip.asset_metadata.size, clip.asset_metadata.fit, clip.asset_metadata.hide_borders]);

  return (
    <div
      className="absolute top-0 size-full overflow-hidden rounded-md bg-cover bg-center"
      style={{ ...backgroundStyle }}
      id="player-container-preview-focus"
    >
      <div className="relative grid h-full items-start" data-testid="focus-layout-preview">
        {finalConfig.map(
          (config, index) =>
            sources[index] && (
              <div key={index} style={config.containerStyle as CSSProperties}>
                <img src={sources[index]} alt={`Speaker ${index + 1}`} style={config.style as CSSProperties} />
              </div>
            )
        )}
      </div>
    </div>
  );
}
